import type { ReactNode } from "react";
import type { WorkspaceChartTypeEnum } from "../spec-gaps";
import { chartStepOrder, ConceptChain } from "./ConceptChain";
import type { WorkspaceChain } from "./type";

/**
 * The chain a chart series or a table column is built with: {@link ConceptChain} with no tail.
 *
 * A chart and a column ask a concept for the same thing — one value, parameterized — so the whole of this
 * is the parameter steps, in the order these two panels show them ({@link chartStepOrder}). What is left
 * here is the rule about *which* concepts may be picked, which is the panel's and not the chain's.
 */
export function WorkspaceChartChain(props: {
	initialValue?: WorkspaceChain;
	onChange(newWorkspaceChain: WorkspaceChain): void;
	autogenerateName: boolean;
	allowedConcepts?: "ALL" | "METRICS_ONLY" | "PROPERTIES_ONLY";
	hideConceptSourceSelection?: boolean; // remove this in favor of multiple source chain
	/**
	 * Offer the "All" leaf on every exposure level the concept declares breakable, turning the last step
	 * of the chain into "expand this level" instead of "pin it" — which is the whole of the
	 * stacked-column configuration.
	 */
	allowBreakdown?: boolean;
	/**
	 * Which chart the chain is describing, because that decides *which parameters the concept accepts*.
	 * Optional and `HISTOGRAM` by default, so the table's column picker — which never draws a line — keeps
	 * reading the concept's own fields with no argument to pass.
	 */
	chartType?: WorkspaceChartTypeEnum;
}): ReactNode[] {
	const { allowedConcepts = "ALL" } = props;
	return ConceptChain<WorkspaceChain>({
		...props,
		allowedConcepts,
		stepOrder: chartStepOrder,
		// A shared concept is already parameterized by whoever shared it, so it cannot be re-parameterized
		// into a column or a series of one's own. The metrics-only picker offers none of them anyway.
		optionsToDisable: allowedConcepts === "METRICS_ONLY" ? undefined : (concept) => concept.shared ?? false,
	});
}
